import { authorizedAPI } from '@/config/axios.config';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UtmCategory {
  id: string;
  name: string;
  description?: string;
  iconType: 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT';
  iconValue: string;
  iconValueDark?: string;
  iconColor?: string;
  iconBgColor?: string;
  style?: Record<string, any>;
  groups?: UtmGroup[];
}

export interface UtmGroup {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  description?: string;
  iconType: 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT';
  iconValue: string;
  iconValueDark?: string;
  iconColor?: string;
  iconBgColor?: string;
  style?: Record<string, any>;
  category?: UtmCategory;
}

export interface UtmZone {
  id: string;
  groupId: string;
  status: 'NOT_PUBLISHED' | 'PUBLISHED';
  zoneCode: string;
  zoneName: string;
  activeStart?: string;
  activeEnd?: string;
  description?: string;
  style?: Record<string, any>;
  geometry?: {
    id: string;
    geom: any;
    lowerBoundM?: number;
    lowerRef?: 'AGL' | 'AMSL';
    upperBoundM?: number;
    upperRef?: 'AGL' | 'AMSL';
  };
  impacts?: UtmImpact[];
  group?: UtmGroup;
}

export interface UtmImpact {
  id: string;
  name: string;
  type?: 'INTERUSSP';
  level: 'NOTAM' | 'AUTHORISATION' | 'NOTIFICATION' | 'NOTHING_TO_DO';
  linkType: 'CATEGORY' | 'GROUP' | 'ZONE';
  linkedIds?: string[];
  linkedEntities?: Array<Record<string, any>>;
  status: 'CHECKED' | 'UNCHECKED';
  intersectionRequirement: 'MUST_BE_WITHIN' | 'MUST_BE_OUTSIDE';
  triggerZones?: Array<{
    id: string;
    zoneCode: string;
    zoneName: string;
    groupId: string;
    group?: {
      id: string;
      code: string;
      name: string;
    };
  }>;
  description?: string;
  zones?: UtmZone[];
}

export interface UtmIntersectionResult {
  zones: UtmZone[];
  impacts: UtmImpact[];
}

export interface UtmZoneFilters {
  groupCode?: string;
  status?: string;
  activeNow?: boolean;
  page?: number;
  limit?: number;
  search?: string;
}

export interface UtmImpactZoneFilters {
  page?: number;
  limit?: number;
  search?: string;
  groupCode?: string;
  categoryId?: string;
  status?: string;
}

export interface UtmMapSettings {
  longitude: number;
  latitude: number;
  zoom: number;
}

// ─── Categories ──────────────────────────────────────────────────────────────

export const fetchCategories = async (): Promise<UtmCategory[]> => {
  const { data } = await authorizedAPI.get<UtmCategory[]>('/utm/categories');
  return data;
};

export const fetchUtmMapSettings = async (): Promise<UtmMapSettings> => {
  const { data } = await authorizedAPI.get<UtmMapSettings>('/utm/map-settings');
  return data;
};

export const updateUtmMapSettings = async (
  payload: UtmMapSettings
): Promise<UtmMapSettings> => {
  const { data } = await authorizedAPI.patch<UtmMapSettings>('/utm/map-settings', payload);
  return data;
};

export const createCategory = async (category: Partial<UtmCategory>): Promise<UtmCategory> => {
  const { data } = await authorizedAPI.post<UtmCategory>('/utm/categories', category);
  return data;
};

export const updateCategory = async (id: string, updates: Partial<UtmCategory>): Promise<UtmCategory> => {
  const { data } = await authorizedAPI.patch<UtmCategory>(`/utm/categories/${id}`, updates);
  return data;
};

export const uploadCategoryIcon = async (id: string, file: File): Promise<UtmCategory> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await authorizedAPI.post<UtmCategory>(`/utm/categories/${id}/icon`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  await authorizedAPI.delete(`/utm/categories/${id}`);
};

// ─── Groups ──────────────────────────────────────────────────────────────────

export const fetchGroups = async (categoryId?: string): Promise<UtmGroup[]> => {
  const params = categoryId ? `?categoryId=${categoryId}` : '';
  const { data } = await authorizedAPI.get<UtmGroup[]>(`/utm/groups${params}`);
  return data;
};

export const createGroup = async (group: Partial<UtmGroup>): Promise<UtmGroup> => {
  const { data } = await authorizedAPI.post<UtmGroup>('/utm/groups', group);
  return data;
};

export const updateGroup = async (id: string, updates: Partial<UtmGroup>): Promise<UtmGroup> => {
  const { data } = await authorizedAPI.patch<UtmGroup>(`/utm/groups/${id}`, updates);
  return data;
};

export const uploadGroupIcon = async (id: string, file: File): Promise<UtmGroup> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await authorizedAPI.post<UtmGroup>(`/utm/groups/${id}/icon`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const deleteGroup = async (id: string): Promise<void> => {
  await authorizedAPI.delete(`/utm/groups/${id}`);
};

// ─── Zones ───────────────────────────────────────────────────────────────────

export const fetchZones = async (filters?: UtmZoneFilters): Promise<PaginatedResponse<UtmZone>> => {
  const params = new URLSearchParams();
  if (filters?.groupCode) params.append('groupCode', filters.groupCode);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.activeNow !== undefined) params.append('activeNow', String(filters.activeNow));
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.search) params.append('search', filters.search);

  const { data } = await authorizedAPI.get<PaginatedResponse<UtmZone>>(`/utm/zones?${params.toString()}`);
  return data;
};

export const fetchZone = async (id: string): Promise<UtmZone> => {
  const { data } = await authorizedAPI.get<UtmZone>(`/utm/zones/${id}`);
  return data;
};

export const createZone = async (zone: Partial<UtmZone>): Promise<UtmZone> => {
  const { data } = await authorizedAPI.post<UtmZone>('/utm/zones', zone);
  return data;
};

export const updateZone = async (id: string, updates: Partial<UtmZone>): Promise<UtmZone> => {
  const { data } = await authorizedAPI.patch<UtmZone>(`/utm/zones/${id}`, updates);
  return data;
};

export const publishZone = async (id: string): Promise<void> => {
  await authorizedAPI.post(`/utm/zones/${id}/publish`);
};

export const unpublishZone = async (id: string): Promise<void> => {
  await authorizedAPI.post(`/utm/zones/${id}/unpublish`);
};

export const deleteZone = async (id: string): Promise<void> => {
  await authorizedAPI.delete(`/utm/zones/${id}`);
};

export const uploadGeometry = async (zoneId: string, geojson: any): Promise<void> => {
  await authorizedAPI.put(`/utm/zones/${zoneId}/geometry`, geojson);
};

export const updateGeometryBounds = async (
  zoneId: string,
  bounds: {
    lowerBoundM?: number;
    lowerRef?: 'AGL' | 'AMSL';
    upperBoundM?: number;
    upperRef?: 'AGL' | 'AMSL';
  }
): Promise<void> => {
  await authorizedAPI.patch(`/utm/zones/${zoneId}/geometry/bounds`, bounds);
};

// ─── Impacts ─────────────────────────────────────────────────────────────────

export const fetchImpacts = async (): Promise<UtmImpact[]> => {
  const { data } = await authorizedAPI.get<UtmImpact[]>('/utm/impacts');
  return data;
};

export const createImpact = async (impact: Partial<UtmImpact>): Promise<UtmImpact> => {
  const { data } = await authorizedAPI.post<UtmImpact>('/utm/impacts', impact);
  return data;
};

export const updateImpact = async (id: string, updates: Partial<UtmImpact>): Promise<UtmImpact> => {
  const { data } = await authorizedAPI.patch<UtmImpact>(`/utm/impacts/${id}`, updates);
  return data;
};

export const deleteImpact = async (id: string): Promise<void> => {
  await authorizedAPI.delete(`/utm/impacts/${id}`);
};

export const attachZoneToImpact = async (impactId: string, zoneId: string): Promise<void> => {
  await authorizedAPI.post(`/utm/impacts/${impactId}/zones/${zoneId}`);
};

export const detachZoneFromImpact = async (impactId: string, zoneId: string): Promise<void> => {
  await authorizedAPI.delete(`/utm/impacts/${impactId}/zones/${zoneId}`);
};

export const fetchImpactZones = async (
  impactId: string,
  filters?: UtmImpactZoneFilters
): Promise<PaginatedResponse<UtmZone>> => {
  const params = new URLSearchParams();
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.search) params.append('search', filters.search);
  if (filters?.groupCode) params.append('groupCode', filters.groupCode);
  if (filters?.categoryId) params.append('categoryId', filters.categoryId);
  if (filters?.status) params.append('status', filters.status);

  const { data } = await authorizedAPI.get<PaginatedResponse<UtmZone>>(
    `/utm/impacts/${impactId}/zones/linked?${params.toString()}`
  );
  return data;
};

export const fetchAvailableZones = async (
  impactId: string,
  filters?: UtmImpactZoneFilters
): Promise<PaginatedResponse<UtmZone>> => {
  const params = new URLSearchParams();
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.search) params.append('search', filters.search);
  if (filters?.groupCode) params.append('groupCode', filters.groupCode);
  if (filters?.categoryId) params.append('categoryId', filters.categoryId);
  if (filters?.status) params.append('status', filters.status);

  const { data } = await authorizedAPI.get<PaginatedResponse<UtmZone>>(
    `/utm/impacts/${impactId}/zones/available?${params.toString()}`
  );
  return data;
};

export const getZoneImpacts = async (zoneId: string): Promise<UtmImpact[]> => {
  const { data } = await authorizedAPI.get<UtmImpact[]>(`/utm/zones/${zoneId}/impacts`);
  return data;
};

export const attachImpactToZone = async (zoneId: string, impactId: string): Promise<void> => {
  await authorizedAPI.post(`/utm/zones/${zoneId}/impacts/${impactId}`);
};

export const detachImpactFromZone = async (zoneId: string, impactId: string): Promise<void> => {
  await authorizedAPI.delete(`/utm/zones/${zoneId}/impacts/${impactId}`);
};

// ─── Analysis ────────────────────────────────────────────────────────────────

export const queryIntersections = async (
  geometry: any,
  filters?: Record<string, any>
): Promise<UtmIntersectionResult> => {
  const { data } = await authorizedAPI.post<UtmIntersectionResult>('/utm/analysis/intersections', {
    geometry,
    filters: filters || {},
  });
  return data;
};

// ─── File Upload ─────────────────────────────────────────────────────────────

export const uploadTemporaryFile = async (
  file: File
): Promise<{ url: string; iconValue: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await authorizedAPI.post<{ url: string; iconValue: string }>(
    '/utm/files/upload/temporary',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};
